import { styles } from "../../styles";
import { DAYS, MONTHS } from "../../lib/constants";
import { minutesToLabel, ymd } from "../../lib/time";
import { zonedWallTimeToUtc, toUtcStamp } from "../../lib/tz";

export interface ConfirmedBooking {
  dogName: string;
  breed: string;
  ownerName: string;
  phone: string;
  email: string;
  date: Date;
  start: number;
  end: number;
  timezone: string;
  shopName: string;
  shopEmail: string;
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div style={styles.sumRow}>
      <dt style={styles.sumK}>{k}</dt>
      <dd style={styles.sumV}>{v}</dd>
    </div>
  );
}

export function Confirmation({
  booking, onDone,
}: {
  booking: ConfirmedBooking;
  onDone: () => void;
}) {
  const { dogName, breed, ownerName, phone, email, date, start, end, timezone, shopName, shopEmail } = booking;

  // Build the convenience invite against the shop timezone, using the same UID
  // the server used, so the customer's downloaded .ics matches the emailed one
  // exactly (same event, same instant) no matter the customer's own timezone.
  const dateYmd = ymd(date);
  const [y, m, d] = dateYmd.split("-").map(Number);
  const dtStart = toUtcStamp(zonedWallTimeToUtc(y, m, d, start, timezone));
  const dtEnd = toUtcStamp(zonedWallTimeToUtc(y, m, d, end, timezone));
  const uid = `${dateYmd}-${start}@radiance`;
  const title = `Grooming: ${dogName} (${ownerName})`;
  const details = `Dog: ${dogName}\nBreed: ${breed}\nOwner: ${ownerName}\nPhone: ${phone}`;

  const ics = [
    "BEGIN:VCALENDAR", "VERSION:2.0", `PRODID:-//${shopName}//Radiance Grooming//EN`,
    "CALSCALE:GREGORIAN", "METHOD:REQUEST", "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${dtStart}`, `DTSTART:${dtStart}`, `DTEND:${dtEnd}`,
    `SUMMARY:${title}`, `DESCRIPTION:${details.replace(/\n/g, "\\n")}`,
    `LOCATION:${shopName}`,
    `ORGANIZER;CN=${shopName}:mailto:${shopEmail}`,
    `ATTENDEE;CN=${ownerName};RSVP=TRUE:mailto:${email}`,
    `ATTENDEE;CN=${shopName};RSVP=TRUE:mailto:${shopEmail}`,
    "STATUS:CONFIRMED", "END:VEVENT", "END:VCALENDAR",
  ].join("\r\n");

  const download = () => {
    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "radiance-appointment.ics";
    a.click();
    URL.revokeObjectURL(url);
  };

  const gcal = () => {
    const u = new URL("https://calendar.google.com/calendar/render");
    u.searchParams.set("action", "TEMPLATE");
    u.searchParams.set("text", title);
    u.searchParams.set("dates", `${dtStart}/${dtEnd}`);
    u.searchParams.set("details", details);
    u.searchParams.set("location", shopName);
    window.open(u.toString(), "_blank", "noopener");
  };

  return (
    <div style={styles.confirmWrap} className="fade">
      <div style={styles.confirmCard}>
        <div style={styles.check} aria-hidden="true">
          <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
            <path d="M5 12.5l4.2 4.2L19 7" stroke="white" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <h1 style={styles.confirmTitle}>You're booked.</h1>
        <p style={styles.confirmLede}>
          {DAYS[date.getDay()]}, {MONTHS[date.getMonth()]} {date.getDate()} at {minutesToLabel(start)}.
        </p>

        <dl style={styles.summary}>
          <Row k="Dog" v={dogName} />
          <Row k="Breed" v={breed} />
          <Row k="Owner" v={ownerName} />
          <Row k="Phone" v={phone} />
          <Row k="Invite to" v={email} />
        </dl>

        <p style={styles.confirmNote}>
          We have emailed a calendar invite to you and the shop, so you both have it. You can also add it now:
        </p>
        <div style={styles.confirmBtns}>
          <button className="primary" style={styles.primary} onClick={gcal}>Add to Google Calendar</button>
          <button className="ghost" style={styles.ghost} onClick={download}>Download invite (.ics)</button>
        </div>
        <button className="link" style={styles.linkBtn} onClick={onDone}>Book another appointment</button>
      </div>
    </div>
  );
}
